// components/OnboardingFlow.jsx
import { useState } from 'react';
import { Icons } from './UI';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to GoldenScope',
    subtitle: 'California State Contract Management',
    icon: '🏛',
    skippable: false,
  },
  {
    id: 'profile',
    title: 'Set Up Your Profile',
    subtitle: 'Your agency and contact information',
    icon: '👤',
    skippable: true,
  },
  {
    id: 'paal',
    title: 'Upload Your PAAL',
    subtitle: 'Purchasing Authority Approval Letter',
    icon: '📋',
    skippable: true,
  },
  {
    id: 'library',
    title: 'Load the SCM',
    subtitle: 'State Contracting Manual Vol 1 & 2',
    icon: '📚',
    skippable: true,
  },
  {
    id: 'done',
    title: "You're Ready",
    subtitle: 'Start managing contracts',
    icon: '✅',
    skippable: false,
  },
];

export default function OnboardingFlow({ profile, paal, library, onComplete, onSkip, setView }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const hasPaal = paal?.nonIT > 0 || paal?.it > 0;
  const hasScm = library?.scm_vol1 || library?.scm_vol2;
  const hasProfile = !!(profile?.contactName && profile?.contactEmail && profile?.agencyName);

  function next() {
    if (isLast) { onComplete(); return; }
    setStep(s => s + 1);
  }

  function goToStep(action) {
    switch (action) {
      case 'profile': setView('profile'); onSkip(); break;
      case 'paal': setView('paal'); onSkip(); break;
      case 'library': setView('library'); onSkip(); break;
      default: break;
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(10,20,40,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 24,
    }}>
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--r-xl, 16px)',
        width: '100%', maxWidth: 560, overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
      }}>

        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--slate-100)' }}>
          <div style={{
            height: '100%', background: 'var(--gold-500)',
            width: ((step / (STEPS.length - 1)) * 100) + '%',
            transition: 'width 0.4s ease',
          }} />
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0 0' }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{
              width: i === step ? 20 : 8, height: 8, borderRadius: 99,
              background: i <= step ? 'var(--navy-700)' : 'var(--slate-200)',
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '24px 36px 32px' }}>

          {/* Welcome step */}
          {current.id === 'welcome' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🏛</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 8 }}>
                Welcome to GoldenScope
              </h2>
              <p style={{ fontSize: 14, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 8 }}>
                Your purpose-built California state procurement and contract management platform.
              </p>
              <p style={{ fontSize: 13.5, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 24 }}>
                This quick setup takes about 2 minutes and gets Penny fully configured for your agency. You can skip any step and come back later.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  ['📋', 'SCM-aligned workflow'],
                  ['✒', 'Penny AI drafting'],
                  ['🏛', 'DGS routing'],
                  ['📊', 'Analytics'],
                ].map(([icon, label]) => (
                  <div key={label} style={{ padding: '6px 14px', background: 'var(--navy-50)', borderRadius: 99, fontSize: 12.5, color: 'var(--navy-700)' }}>
                    {icon} {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Profile step */}
          {current.id === 'profile' && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12, textAlign: 'center' }}>👤</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 6, textAlign: 'center' }}>Set Up Your Profile</h2>
              <p style={{ fontSize: 13.5, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 20, textAlign: 'center' }}>
                Your profile auto-populates the STD 215, STD 213, and every email Penny drafts — agency name, contact info, signatory, and billing officer.
              </p>
              {hasProfile ? (
                <div style={{ padding: '14px 16px', background: 'var(--green-50)', border: '1.5px solid var(--green-200)', borderRadius: 'var(--r-md)', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--green-700)' }}>Profile complete</div>
                    <div style={{ fontSize: 12, color: 'var(--green-600)' }}>{profile.contactName} · {profile.agencyName}</div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '14px 16px', background: 'var(--orange-50)', border: '1.5px solid var(--orange-200)', borderRadius: 'var(--r-md)', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--orange-700)', marginBottom: 4 }}>Profile not set up yet</div>
                  <div style={{ fontSize: 12.5, color: 'var(--orange-600)', lineHeight: 1.6 }}>
                    Without a profile, your STD 215 and email drafts will have blank contact fields. Takes about 2 minutes to fill in.
                  </div>
                </div>
              )}
              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => goToStep('profile')}>
                {hasProfile ? '✏ Edit Profile' : '→ Go to User Profile'}
              </button>
            </div>
          )}

          {/* PAAL step */}
          {current.id === 'paal' && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12, textAlign: 'center' }}>📋</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 6, textAlign: 'center' }}>Upload Your PAAL</h2>
              <p style={{ fontSize: 13.5, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 20, textAlign: 'center' }}>
                Your Purchasing Authority Approval Letter tells GoldenScope when to flag contracts for DGS/OLS routing. Penny will read the PDF and extract your thresholds automatically.
              </p>
              {hasPaal ? (
                <div style={{ padding: '14px 16px', background: 'var(--green-50)', border: '1.5px solid var(--green-200)', borderRadius: 'var(--r-md)', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--green-700)' }}>PAAL configured</div>
                    <div style={{ fontSize: 12, color: 'var(--green-600)' }}>
                      Non-IT: ${(paal.nonIT || 0).toLocaleString()} · IT: ${(paal.it || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '14px 16px', background: 'var(--orange-50)', border: '1.5px solid var(--orange-200)', borderRadius: 'var(--r-md)', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--orange-700)', marginBottom: 4 }}>PAAL not uploaded</div>
                  <div style={{ fontSize: 12.5, color: 'var(--orange-600)', lineHeight: 1.6 }}>
                    Without your PAAL, DGS routing flags use default thresholds ($50K Non-IT, $200K IT) which may not match your actual delegation.
                  </div>
                </div>
              )}
              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => goToStep('paal')}>
                {hasPaal ? '✏ Review PAAL Settings' : '→ Go to PAAL & Authority'}
              </button>
            </div>
          )}

          {/* Library step */}
          {current.id === 'library' && (
            <div>
              <div style={{ fontSize: 40, marginBottom: 12, textAlign: 'center' }}>📚</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 6, textAlign: 'center' }}>Load the State Contracting Manual</h2>
              <p style={{ fontSize: 13.5, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 20, textAlign: 'center' }}>
                The SCM is Penny's compliance backbone. Upload SCM Vol 1 (Non-IT) and Vol 2 (IT) so Penny can generate a contract-specific compliance brief before every draft.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {[
                  { key: 'scm_vol1', label: 'SCM Vol 1 — Non-IT Services', desc: 'Required for RFQ, IFB, NCB, and other Non-IT procurements' },
                  { key: 'scm_vol2', label: 'SCM Vol 2 — IT Services', desc: 'Required for IT procurements and software contracts' },
                ].map(item => {
                  const loaded = !!library?.[item.key];
                  return (
                    <div key={item.key} style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', background: loaded ? 'var(--green-50)' : 'var(--slate-50)', border: '1.5px solid ' + (loaded ? 'var(--green-200)' : 'var(--slate-200)'), display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: 20 }}>{loaded ? '✅' : '⬜'}</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: loaded ? 'var(--green-700)' : 'var(--navy-800)' }}>{item.label}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => goToStep('library')}>
                {hasScm ? '✏ Manage Reference Library' : '→ Go to Reference Library'}
              </button>
            </div>
          )}

          {/* Done step */}
          {current.id === 'done' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 8 }}>
                You're All Set!
              </h2>
              <p style={{ fontSize: 14, color: 'var(--slate-500)', lineHeight: 1.7, marginBottom: 20 }}>
                GoldenScope is ready to go. Create your first contract and let Penny guide you through the procurement workflow.
              </p>

              {/* Setup summary */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24, textAlign: 'left' }}>
                {[
                  [hasProfile, 'Profile configured — STD 215 and emails will auto-populate'],
                  [hasPaal, 'PAAL loaded — DGS routing thresholds active'],
                  [hasScm, 'SCM loaded — Penny will generate compliance briefs'],
                ].map(([done, label], i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: done ? 'var(--green-700)' : 'var(--slate-400)' }}>
                    <span>{done ? '✅' : '⬜'}</span>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {(!hasProfile || !hasPaal || !hasScm) && (
                <div style={{ fontSize: 12, color: 'var(--slate-400)', marginBottom: 16, fontStyle: 'italic' }}>
                  You can complete any unchecked steps anytime from the left sidebar.
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: current.id === 'welcome' || current.id === 'done' ? 'center' : 'space-between' }}>
            {current.skippable && (
              <button className="btn btn-ghost" onClick={next} style={{ color: 'var(--slate-400)' }}>
                Skip for now →
              </button>
            )}
            {(current.id === 'welcome' || current.id === 'done') && (
              <button className="btn btn-gold" style={{ minWidth: 160, justifyContent: 'center' }} onClick={next}>
                {current.id === 'done' ? "Let's go →" : 'Get Started →'}
              </button>
            )}
            {current.skippable && step > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setStep(s => s - 1)} style={{ color: 'var(--slate-300)' }}>
                ← Back
              </button>
            )}
          </div>

          {/* Global skip */}
          {current.id !== 'done' && current.id !== 'welcome' && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button onClick={onSkip} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--slate-300)', cursor: 'pointer', textDecoration: 'underline' }}>
                Skip setup entirely
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
